// src/pages/AddictionDetectionResults.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/AddictionDetectionResults.css";
import logoMirai from "../assets/mirai_logo.png";
import 'bootstrap-icons/font/bootstrap-icons.css';
import Navbar from '../components/Navbar';
import { PLATFORM_PDF_FOOTER } from '../constants/platform';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

// React-PDF
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

// === Estilos PDF ===
const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 12, fontFamily: "Helvetica" },
  header: { textAlign: "center", marginBottom: 20 },
  title: { fontSize: 18, fontWeight: "bold", color: "#004d40", marginTop: 10 },
  logo: { width: 60, height: 60, margin: "0 auto" },
  studentInfo: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 8,
    color: "#333",
  },
  card: {
    border: "1pt solid #ccc",
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
  },
  recommendation: { fontSize: 11, marginTop: 4, textAlign: "justify" },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    fontSize: 9,
    textAlign: "center",
    color: "#666",
  },
});

// === Documento PDF ===
const ResultsPDF = ({ user, results }) => {
  const date = new Date().toLocaleDateString();

  let recomendaciones = [];
  switch (results.riskLevel) {
    case "Riesgo Bajo":
      recomendaciones = [
        "No se han identificado signos significativos de adicción.",
        "Se recomienda mantener hábitos saludables y continuar con tus actividades normales.",
        "Observa cualquier cambio en tu comportamiento y, si lo notas, consulta a un especialista.",
      ];
      break;
    case "Riesgo Moderado":
      recomendaciones = [
        "Se han detectado indicios de comportamientos adictivos.",
        "Es aconsejable que evalúes tus hábitos y consideres hablar con un orientador o especialista.",
        "Participa en talleres de prevención y busca información para mejorar tu situación.",
      ];
      break;
    case "Riesgo Alto":
      recomendaciones = [
        "Se han identificado signos claros de comportamientos adictivos que pueden afectar tu bienestar.",
        "Es fundamental que busques ayuda profesional de inmediato.",
        "Contacta a un especialista en adicciones para una evaluación detallada y seguimiento personalizado.",
      ];
      break;
    default:
      recomendaciones = ["No se encontraron recomendaciones específicas."];
  }

  const getLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case "Riesgo Alto": return "#dc3545";
      case "Riesgo Moderado": return "#ffc107";
      case "Riesgo Bajo": return "#28a745";
      default: return "#6c757d";
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Marca de agua */}
        <Image
          src={logoMirai}
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: 300,
            height: 300,
            opacity: 0.1,
          }}
        />

        {/* Header */}
        <View style={styles.header}>
          <Image src={logoMirai} style={styles.logo} />
          <Text style={styles.title}>Resultados de Detección de Adicciones</Text>
        </View>

        {/* Datos */}
        <View style={styles.studentInfo}>
          <Text>Nombre del estudiante: {user?.name || "N/D"}</Text>
          <Text>Fecha: {date}</Text>
          <Text>Puntaje Total: {results.totalScore} / 100</Text>
          <Text style={{ color: getLevelColor(results.riskLevel), fontWeight: "bold" }}>
            Nivel de Riesgo: {results.riskLevel}
          </Text>
        </View>

        {/* Recomendaciones */}
        <Text style={styles.sectionTitle}>Recomendaciones:</Text>
        <View style={styles.card}>
          {recomendaciones.map((rec, idx) => (
            <Text key={idx} style={styles.recommendation}>• {rec}</Text>
          ))}
        </View>

        {/* Footer */}
        <Text style={styles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

const AddictionDetectionResults = () => {
  const [results, setResults] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("overview");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/me`, {
          headers: { Authorization: token },
        });
        setResults(response.data.AddictionDetectionResults || {});
        setUser(response.data);
      } catch (error) {
        console.error("Error al obtener resultados:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  const getLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case "Riesgo Alto": return "#dc3545";
      case "Riesgo Moderado": return "#ffc107";
      case "Riesgo Bajo": return "#28a745";
      default: return "#6c757d";
    }
  };

  const getLevelIcon = (riskLevel) => {
    switch (riskLevel) {
      case "Riesgo Alto": return "bi-exclamation-triangle-fill";
      case "Riesgo Moderado": return "bi-exclamation-circle-fill";
      case "Riesgo Bajo": return "bi-check-circle-fill";
      default: return "bi-question-circle";
    }
  };

  // Datos para gráfico circular (Gauge)
  const scoreData = {
    labels: ['Puntaje', 'Restante'],
    datasets: [
      {
        data: [results?.totalScore || 0, 100 - (results?.totalScore || 0)],
        backgroundColor: [
          getLevelColor(results?.riskLevel),
          '#e9ecef'
        ],
        borderWidth: 0,
        circumference: 180,
        rotation: 270,
      },
    ],
  };

  // Datos para gráfico de barras (Comparativo)
  const comparisonData = {
    labels: ['Tu Puntaje', 'Riesgo Bajo', 'Riesgo Alto'],
    datasets: [
      {
        label: 'Puntaje',
        data: [results?.totalScore || 0, 20, 50],
        backgroundColor: [
          getLevelColor(results?.riskLevel),
          '#28a745',
          '#dc3545'
        ],
        borderWidth: 0,
        borderRadius: 5,
        barPercentage: 0.6,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <div className="dashboard-layout">
        {/* Sidebar - Standard structure */}
        <div className="dashboard-sidebar">
          <div className="sidebar-header">
            <h3>Detección de Adicciones</h3>
          </div>
          <div className="sidebar-menu">
            <button
              className={`menu-item ${activeSection === "overview" ? "active" : ""}`}
              onClick={() => setActiveSection("overview")}
            >
              <i className="bi bi-speedometer2"></i> Resumen
            </button>
            <button
              className="menu-item return-dashboard"
              onClick={() => navigate("/dashboard")}
            >
              <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
            </button>
          </div>
        </div>

        {/* Main Content - Modern structure */}
        <div className="dashboard-main">
          <div className="adr-container">
            {loading ? (
              <div className="adr-loading">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p>Analizando datos...</p>
              </div>
            ) : !results || results.totalScore === undefined ? (
              <div className="adr-empty">
                <i className="bi bi-clipboard-x"></i>
                <h3>Sin resultados</h3>
                <p>No has completado esta evaluación aún.</p>
                <button className="btn btn-primary" onClick={() => navigate("/dashboard")}>
                  Ir al Dashboard
                </button>
              </div>
            ) : (
              <>
                <div className="adr-header">
                  <h2><i className="bi bi-activity"></i> Análisis de Resultados</h2>
                  <p>Informe detallado de niveles de riesgo y recomendaciones.</p>
                </div>

                {/* Top Stats Row */}
                <div className="adr-stats-grid">
                  <div className="adr-stat-card primary-card">
                    <div className="icon-wrapper">
                      <i className="bi bi-clipboard-data"></i>
                    </div>
                    <div className="content-wrapper">
                      <h3>{results.totalScore}<span className="small-text">/100</span></h3>
                      <p>Puntaje Global</p>
                    </div>
                  </div>

                  <div className="adr-stat-card risk-card" style={{ borderLeftColor: getLevelColor(results.riskLevel) }}>
                    <div className="icon-wrapper" style={{ color: getLevelColor(results.riskLevel), background: `${getLevelColor(results.riskLevel)}20` }}>
                      <i className={`bi ${getLevelIcon(results.riskLevel)}`}></i>
                    </div>
                    <div className="content-wrapper">
                      <h3 style={{ color: getLevelColor(results.riskLevel) }}>{results.riskLevel}</h3>
                      <p>Nivel de Riesgo</p>
                    </div>
                  </div>

                  <div className="adr-stat-card date-card">
                    <div className="icon-wrapper">
                      <i className="bi bi-calendar-check"></i>
                    </div>
                    <div className="content-wrapper">
                      <h3>{new Date().toLocaleDateString()}</h3>
                      <p>Fecha Evaluación</p>
                    </div>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="adr-charts-grid">
                  <div className="adr-chart-card">
                    <div className="card-title">
                      <h4><i className="bi bi-speedometer2"></i> Medidor de Riesgo</h4>
                    </div>
                    <div className="gauge-wrapper">
                      <div className="gauge-chart">
                        <Doughnut
                          data={scoreData}
                          options={{
                            cutout: '75%',
                            plugins: { legend: { display: false }, tooltip: { enabled: false } },
                            maintainAspectRatio: false
                          }}
                        />
                        <div className="gauge-center-text">
                          <h2>{results.totalScore}%</h2>
                        </div>
                      </div>
                      <p className="chart-note">Tu nivel relativo al máximo posible</p>
                    </div>
                  </div>

                  <div className="adr-chart-card">
                    <div className="card-title">
                      <h4><i className="bi bi-bar-chart"></i> Comparativo</h4>
                    </div>
                    <div className="bar-wrapper">
                      <Bar
                        data={comparisonData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: { y: { beginAtZero: true, max: 100 } }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                <div className="adr-recommendations">
                  <h4><i className="bi bi-lightbulb"></i> Recomendaciones</h4>
                  <div className="adr-rec-grid">
                    {results.riskLevel === "Riesgo Bajo" && (
                      <>
                        <div className="adr-rec-card success">
                          <i className="bi bi-check-circle-fill"></i>
                          <div>
                            <h5>Situación Estable</h5>
                            <p>No se detectan signos de alarma. Mantén tus hábitos saludables.</p>
                          </div>
                        </div>
                        <div className="adr-rec-card info">
                          <i className="bi bi-info-circle-fill"></i>
                          <div>
                            <h5>Prevención</h5>
                            <p>Continúa informándote sobre bienestar emocional.</p>
                          </div>
                        </div>
                      </>
                    )}
                    {results.riskLevel === "Riesgo Moderado" && (
                      <>
                        <div className="adr-rec-card warning">
                          <i className="bi bi-exclamation-circle-fill"></i>
                          <div>
                            <h5>Atención Preventiva</h5>
                            <p>Hay indicios leves. Evalúa tus rutinas y busca equilibrio.</p>
                          </div>
                        </div>
                        <div className="adr-rec-card info">
                          <i className="bi bi-people-fill"></i>
                          <div>
                            <h5>Apoyo</h5>
                            <p>Conversar con alguien de confianza puede ser de gran ayuda.</p>
                          </div>
                        </div>
                      </>
                    )}
                    {results.riskLevel === "Riesgo Alto" && (
                      <>
                        <div className="adr-rec-card danger">
                          <i className="bi bi-exclamation-triangle-fill"></i>
                          <div>
                            <h5>Acción Recomendada</h5>
                            <p>Se identifican signos importantes. Buscar orientación profesional es prioritario.</p>
                          </div>
                        </div>
                        <div className="adr-rec-card info">
                          <i className="bi bi-hospital-fill"></i>
                          <div>
                            <h5>Ayuda Especializada</h5>
                            <p>Acude a servicios de bienestar estudiantil o salud mental.</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer Action */}
                <div className="adr-footer">
                  <PDFDownloadLink
                    document={<ResultsPDF user={user} results={results} />}
                    fileName={`Resultados_Adicciones_${new Date().toLocaleDateString().replaceAll("/", "_")}.pdf`}
                    className="text-decoration-none"
                  >
                    {({ loading }) => (
                      <button className="btn btn-primary adr-btn" disabled={loading}>
                        <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-file-earmark-pdf'}`}></i>
                        {loading ? ' Generando PDF...' : ' Descargar Resultados Completo'}
                      </button>
                    )}
                  </PDFDownloadLink>
                </div>

              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddictionDetectionResults;
